import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Eye, ExternalLink, Send, MapPin, Clock, MessageCircle, RefreshCw } from "lucide-react";

const TABS = [
  { id: "pending", label: "समीक्षाधीन" },
  { id: "approved", label: "प्रकाशित" },
  { id: "all", label: "सभी" },
];

export default function ReporterApprovalsPage() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/reporter-submissions?status=${tab}`)
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tab]); // eslint-disable-line

  const approve = async (id) => {
    try { await api.post(`/admin/reporter-submissions/${id}/approve`); toast.success("प्रकाशित कर दिया"); load(); setSelected(null); }
    catch(e) { toast.error(formatError(e)); }
  };
  const reject = async (id) => {
    if (!window.confirm("क्या आप वाकई इस समाचार को हटाना चाहते हैं?")) return;
    try { await api.post(`/admin/reporter-submissions/${id}/reject`); toast.success("अस्वीकृत करके हटाया"); load(); setSelected(null); }
    catch(e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="reporter-approvals-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Send className="w-5 h-5 text-emerald-600"/>रिपोर्टर सबमिशन</h1>
          <p className="text-sm text-slate-500 mt-1">फ़ील्ड रिपोर्टर द्वारा भेजी हुई ख़बरें — यहाँ से एक क्लिक में प्रकाशित/अस्वीकृत करें</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-md border border-slate-200 hover:bg-slate-50" title="रीलोड" data-testid="approvals-refresh">
            <RefreshCw className="w-4 h-4"/>
          </button>
          <Link to="/admin/reporter-map" className="text-sm text-slate-600 hover:text-[#0F172A] underline">कवरेज मानचित्र देखें →</Link>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-200 bg-slate-50">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm font-bold border-b-2 transition ${
                tab === t.id ? "border-[#0F172A] text-[#0F172A] bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
              }`} data-testid={`approvals-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500">लोड हो रहा है…</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center text-slate-500 italic">कोई सबमिशन नहीं</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((s) => (
              <div key={s.id} className="p-4 md:p-5 hover:bg-slate-50 grid grid-cols-1 md:grid-cols-[100px_1fr_auto] gap-4 items-start" data-testid={`sub-row-${s.id}`}>
                <div>
                  {s.image_url ? (
                    <img src={s.image_url} alt="" className="w-full aspect-video object-cover rounded-md"/>
                  ) : (
                    <div className="w-full aspect-video rounded-md bg-slate-100 flex items-center justify-center text-slate-300 text-[10px]">कोई फ़ोटो नहीं</div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#EA580C]">{s.category}</div>
                  <div className="font-devnagri-heading font-black text-[#0F172A] text-base leading-snug mt-1">{s.title}</div>
                  <div className="text-sm text-slate-600 mt-1 line-clamp-2">{s.excerpt || (s.content || "").slice(0, 180)}</div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{new Date(s.created_at).toLocaleString("hi-IN")}</span>
                    <span>रिपोर्टर: <b className="text-slate-700">{s.author}</b> · <span className="font-mono">{s.reporter_staff_id}</span></span>
                    {s.district && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{s.district}{s.block ? `, ${s.block}` : ""}</span>}
                  </div>
                </div>
                <div className="flex md:flex-col gap-2 md:min-w-[160px] justify-end">
                  <button onClick={() => setSelected(s)} className="text-xs px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-100 inline-flex items-center gap-1" data-testid={`view-${s.id}`}>
                    <Eye className="w-3.5 h-3.5"/>देखें
                  </button>
                  {s.admin_wa_share_url && (
                    <a href={s.admin_wa_share_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs px-3 py-1.5 border border-emerald-200 text-emerald-700 rounded-md hover:bg-emerald-50 inline-flex items-center gap-1" title="WhatsApp पर शेयर">
                      <MessageCircle className="w-3.5 h-3.5"/>WA
                    </a>
                  )}
                  {!s.is_published ? (
                    <>
                      <button onClick={() => approve(s.id)} className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 inline-flex items-center gap-1" data-testid={`approve-${s.id}`}>
                        <Check className="w-3.5 h-3.5"/>प्रकाशित
                      </button>
                      <button onClick={() => reject(s.id)} className="text-xs px-3 py-1.5 bg-red-100 text-red-700 rounded-md hover:bg-red-200 inline-flex items-center gap-1" data-testid={`reject-${s.id}`}>
                        <X className="w-3.5 h-3.5"/>अस्वीकार
                      </button>
                    </>
                  ) : (
                    <a href={`/article/${s.slug}`} target="_blank" rel="noopener noreferrer"
                      className="text-xs px-3 py-1.5 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 inline-flex items-center gap-1">
                      <ExternalLink className="w-3.5 h-3.5"/>देखें
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail dialog */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {selected.image_url && <img src={selected.image_url} alt="" className="w-full aspect-video object-cover rounded-t-xl"/>}
            <div className="p-6">
              <div className="text-[10px] uppercase tracking-widest text-[#EA580C] font-bold">{selected.category}</div>
              <h2 className="font-devnagri-heading font-black text-2xl text-[#0F172A] mt-1">{selected.title}</h2>
              <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                <span>रिपोर्टर: <b>{selected.author}</b> ({selected.reporter_staff_id})</span>
                <span>स्थान: {selected.district || "—"}{selected.block ? `, ${selected.block}` : ""}</span>
                <span>{new Date(selected.created_at).toLocaleString("hi-IN")}</span>
              </div>
              {selected.excerpt && <p className="mt-3 text-sm text-slate-700 leading-relaxed italic">{selected.excerpt}</p>}
              <div className="mt-4 text-slate-700 leading-relaxed whitespace-pre-line font-serif-hi text-[15px]">{selected.content}</div>
              <div className="mt-6 flex flex-wrap gap-2 justify-end">
                <button onClick={() => setSelected(null)} className="px-4 py-2 text-sm border border-slate-200 rounded-md hover:bg-slate-50">बंद करें</button>
                {!selected.is_published && (
                  <>
                    <button onClick={() => reject(selected.id)} className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 inline-flex items-center gap-1"><X className="w-4 h-4"/>अस्वीकार</button>
                    <button onClick={() => approve(selected.id)} className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 inline-flex items-center gap-1"><Check className="w-4 h-4"/>प्रकाशित करें</button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
